import { apiGet, extractMessage } from "../client";
import {
  filterInvoiceFinanceSectorOptions,
  resolveInvoiceFinanceSector,
} from "../invoiceFinanceSourcing.js";

/**
 * Invoice Finance sourcing — GET /api/method/dynesourcerapi.api.get_invoice_finance_details
 * Query params use Frappe-style filters (e.g. ip_percent__gte=80).
 * Override via REACT_APP_INVOICE_FINANCE_DETAILS_PATH.
 */
export const INVOICE_FINANCE_DETAILS_PATH =
  process.env.REACT_APP_INVOICE_FINANCE_DETAILS_PATH ||
  "/api/method/dynesourcerapi.api.get_invoice_finance_details";

function toArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.products)) return payload.products;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.invoice_finance_details)) return payload.invoice_finance_details;
  return [];
}

function pickProducts(root) {
  if (!root || typeof root !== "object") return [];
  return toArray(
    root.products ??
      root.invoice_finance_details ??
      root.matched_products ??
      root.data ??
      root.results ??
      root
  );
}

function pickSectors(root, products = []) {
  if (root && typeof root === "object") {
    const candidates =
      root.sectors ??
      root.sector_options ??
      root.filter_options?.sectors ??
      root.filters?.sectors;
    if (Array.isArray(candidates) && candidates.length > 0) {
      return filterInvoiceFinanceSectorOptions(candidates);
    }
  }
  const fromProducts = filterInvoiceFinanceSectorOptions([
    ...new Set(
      (products || [])
        .map((p) => p?.sector)
        .filter((s) => s != null && String(s).trim() !== "")
    ),
  ]);
  return fromProducts.sort((a, b) => String(a).localeCompare(String(b)));
}

/** Request the full catalogue in one call when the API allows; page if the server caps lower. */
const INVOICE_FINANCE_PAGE_SIZE = 1000000;

function unwrapInvoiceFinanceRoot(peeled) {
  if (!peeled || typeof peeled !== "object") return {};
  // extractMessage already unwraps `{ message: {...} }`; keep a nested fallback.
  if (peeled.message && typeof peeled.message === "object" && !Array.isArray(peeled.data)) {
    return peeled.message;
  }
  return peeled;
}

function parseInvoiceFinanceResponse(res) {
  const peeled = extractMessage(res);

  if (peeled && typeof peeled === "object" && peeled.success === false) {
    const err = new Error(peeled.error || peeled.message || "Failed to load sourcing products.");
    err.details = peeled;
    throw err;
  }

  const root = unwrapInvoiceFinanceRoot(peeled);
  const products = pickProducts(root);
  const sectors = pickSectors(root, products);
  const pagination = root?.pagination || {};
  const totalRaw = Number(pagination.total ?? root?.total ?? root?.count);
  const total = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : products.length;
  const pageLimit =
    Number(pagination.limit) ||
    Number(pagination.page_size) ||
    Number(pagination.count) ||
    products.length ||
    INVOICE_FINANCE_PAGE_SIZE;

  return { products, sectors, total, pageLimit, pagination, raw: root };
}

/** Map sourcing form payload → GET query string for get_invoice_finance_details. */
export function buildInvoiceFinanceQueryParams(payload = {}) {
  const params = {};
  const filters = payload.filters || {};

  // Sector is matched client-side (smart short↔full labels, e.g. "Recruitment"
  // vs "Recruitment and staffing"). Do not send as an exact API filter — that
  // drops products whose sector string doesn't match the canonical name.

  // Turnover / facility / AB are used client-side for calc + min/max facility matching.
  // Do not send them as API filters — that shrinks the catalogue before we can match bands.

  // Credit, confidential, HMRC arrears, and previous liquidations use inclusive
  // client-side matching (Yes includes No/better bands). Exact API filters would
  // drop products before those rules can run.

  // Contract term: only when explicitly set (> 0). DB has both 12- and 24-month products.
  const contractTermMonths = Number(filters.contract_term_months);
  if (Number.isFinite(contractTermMonths) && contractTermMonths > 0) {
    params.contract_term_months = contractTermMonths;
  }

  // Opt-in eligibility flags only (defaults-on flags are applied client-side).
  const OPT_IN_BOOL_FILTER_KEYS = [
    "contractual_debt_agreements",
    "proof_of_debt",
    "current_short_term_debt",
    "selective_invoicing",
    "debtor_protection",
  ];
  for (const key of OPT_IN_BOOL_FILTER_KEYS) {
    if (filters[key] === 1 || filters[key] === true) {
      params[key] = 1;
    }
  }

  if (filters.funding_ledger_type === "full") {
    params.part_full_fund_ledger = "Full";
  } else if (filters.funding_ledger_type === "part") {
    params.part_full_fund_ledger = "Part";
  }

  // Home owner: only restrict when user selects "No" (Yes = no API filter).
  if (String(filters.home_owner || "").trim().toLowerCase() === "no") {
    params.home_owner = "No";
  }

  return params;
}

export function buildInvoiceFinanceSourcingPayload({
  sector,
  facilityNum,
  turnoverNum,
  additionalBorrowingType,
  additionalBorrowingNum,
  filters = {},
}) {
  const hasAdditionalBorrowing = Number.isFinite(additionalBorrowingNum) && additionalBorrowingNum > 0;

  return {
    sector: sector ? resolveInvoiceFinanceSector(sector) || sector : undefined,
    projected_turnover: turnoverNum,
    facility_required: facilityNum,
    additional_borrowing_type: hasAdditionalBorrowing ? additionalBorrowingType : undefined,
    additional_borrowing: hasAdditionalBorrowing ? additionalBorrowingNum : undefined,
    filters: {
      credit_worthiness: filters.creditWorthiness,
      credit_terms_days: filters.creditTermsDays,
      contract_term_months: filters.contractTermMonths,
      contractual_debt_agreements: filters.contractualDebtAgreements ? 1 : 0,
      proof_of_debt: filters.proofOfDebt ? 1 : 0,
      current_short_term_debt: filters.currentShortTermDebt ? 1 : 0,
      selective_invoicing: filters.selectiveInvoicing ? 1 : 0,
      debtor_protection: filters.debtorProtection ? 1 : 0,
      funding_ledger_type: filters.fundingLedgerType || undefined,
      hmrc_arrears: filters.hmrcArrears ? 1 : 0,
      previous_liquidated_co: filters.previousLiquidatedCo ? 1 : 0,
      director_experience: filters.directorExperience ? 1 : 0,
      personal_guarantees: filters.personalGuarantees ? 1 : 0,
      confidential_facility: filters.confidentialFacility ? 1 : 0,
      concentration_limit: filters.concentrationLimit,
      export_ledger: filters.exportLedger,
      home_owner: filters.homeOwner || undefined,
    },
  };
}

/**
 * @returns {Promise<{ products: Array, sectors: string[], count: number, raw: object, fromCache?: boolean }>}
 */
async function fetchInvoiceFinanceCataloguePage(baseParams, { start, page }) {
  const res = await apiGet(
    INVOICE_FINANCE_DETAILS_PATH,
    {
      ...baseParams,
      limit: INVOICE_FINANCE_PAGE_SIZE,
      limit_page_length: INVOICE_FINANCE_PAGE_SIZE,
      start,
      page,
    },
    { _skipSessionExpiry: true, _guestFallback: true }
  );
  return parseInvoiceFinanceResponse(res);
}

function mergeCataloguePages(pages) {
  const allProducts = [];
  const seenIds = new Set();
  const sectorSet = new Set();
  let total = null;
  let lastRaw = {};

  for (const parsed of pages) {
    if (!parsed) continue;
    lastRaw = parsed.raw || lastRaw;
    (parsed.sectors || []).forEach((s) => sectorSet.add(s));
    if (total == null && Number.isFinite(parsed.total) && parsed.total > 0) {
      total = parsed.total;
    }
    for (const product of parsed.products || []) {
      const id = String(product?.name || product?.id || "").trim();
      const key = id || JSON.stringify(product);
      if (seenIds.has(key)) continue;
      seenIds.add(key);
      allProducts.push(product);
    }
  }

  const sectors = filterInvoiceFinanceSectorOptions(
    [...sectorSet].sort((a, b) => String(a).localeCompare(String(b)))
  );

  return {
    products: allProducts,
    sectors,
    count: allProducts.length,
    total: total ?? allProducts.length,
    raw: lastRaw,
  };
}

/** In-memory catalogue cache — keyed only by API query params (not facility/sector/turnover). */
const invoiceFinanceCatalogueCache = new Map();
const invoiceFinanceCatalogueInflight = new Map();
const INVOICE_FINANCE_CACHE_TTL_MS = 5 * 60 * 1000;

function invoiceFinanceCacheKey(baseParams) {
  return JSON.stringify(baseParams || {});
}

export function peekInvoiceFinanceCatalogueCache(payload = {}) {
  const key = invoiceFinanceCacheKey(buildInvoiceFinanceQueryParams(payload));
  const hit = invoiceFinanceCatalogueCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.savedAt > INVOICE_FINANCE_CACHE_TTL_MS) {
    invoiceFinanceCatalogueCache.delete(key);
    return null;
  }
  return { ...hit.result, fromCache: true };
}

async function fetchAllInvoiceFinanceCataloguePages(baseParams) {
  const first = await fetchInvoiceFinanceCataloguePage(baseParams, { start: 0, page: 1 });
  const firstProducts = first.products || [];
  if (!firstProducts.length) {
    return mergeCataloguePages([first]);
  }

  const pageLimit = Math.max(1, Number(first.pageLimit) || firstProducts.length);
  const total =
    Number.isFinite(first.total) && first.total > 0 ? first.total : null;

  // Single page already complete.
  if (total != null && firstProducts.length >= total) {
    return mergeCataloguePages([first]);
  }
  if (firstProducts.length < pageLimit) {
    return mergeCataloguePages([first]);
  }

  const remainingStarts = [];
  if (total != null && total > firstProducts.length) {
    for (let start = firstProducts.length; start < total; start += pageLimit) {
      remainingStarts.push(start);
      if (remainingStarts.length >= 199) break;
    }
  } else {
    // Total unknown — keep sequential fallback for remaining pages.
    const pages = [first];
    let start = firstProducts.length;
    let page = 2;
    while (page <= 200) {
      const next = await fetchInvoiceFinanceCataloguePage(baseParams, { start, page });
      const pageProducts = next.products || [];
      if (!pageProducts.length) break;
      pages.push(next);
      if (pageProducts.length < pageLimit) break;
      const nextStart = start + pageProducts.length;
      if (nextStart <= start) break;
      start = nextStart;
      page += 1;
    }
    return mergeCataloguePages(pages);
  }

  const rest = await Promise.all(
    remainingStarts.map((start, idx) =>
      fetchInvoiceFinanceCataloguePage(baseParams, { start, page: idx + 2 })
    )
  );

  return mergeCataloguePages([first, ...rest]);
}

/**
 * Loads the IF catalogue once per API-filter set, then serves later calls from memory.
 * Facility / sector / turnover / AB do not change the API key — those are client-side only.
 *
 * @returns {Promise<{ products: Array, sectors: string[], count: number, raw: object, fromCache?: boolean }>}
 */
export async function getInvoiceFinanceSourcingFilters(payload = {}) {
  const baseParams = buildInvoiceFinanceQueryParams(payload);
  const key = invoiceFinanceCacheKey(baseParams);

  const cached = peekInvoiceFinanceCatalogueCache(payload);
  if (cached) return cached;

  if (invoiceFinanceCatalogueInflight.has(key)) {
    return invoiceFinanceCatalogueInflight.get(key);
  }

  const promise = fetchAllInvoiceFinanceCataloguePages(baseParams)
    .then((result) => {
      invoiceFinanceCatalogueCache.set(key, { result, savedAt: Date.now() });
      invoiceFinanceCatalogueInflight.delete(key);
      return { ...result, fromCache: false };
    })
    .catch((err) => {
      invoiceFinanceCatalogueInflight.delete(key);
      throw err;
    });

  invoiceFinanceCatalogueInflight.set(key, promise);
  return promise;
}

/** @deprecated Use INVOICE_FINANCE_DETAILS_PATH */
export const INVOICE_FINANCE_SOURCING_FILTERS_PATH = INVOICE_FINANCE_DETAILS_PATH;
